import './bymo-page-viewer-page.st.css';
import type {BymoTemplatesStore} from "../../../client-app-and-viewer-common/bymo-templates-store/bymo-templates-store";
import type {DoppeDtoBymoLinkViewerData} from "../../../client-server-common/types/doppe-dto-bymo-link-viewer-data";
import {
    doppeDtoBymoLinkViewerDataResolvePageType
} from "../../../client-server-common/types/doppe-dto-bymo-link-viewer-data";
import React from "react";
import type {BymoPageViewerLiveMode} from "../../../components/bymo-page-viewer-context/bymo-page-viewer-context";
import {BymoPageViewerContext} from "../../../components/bymo-page-viewer-context/bymo-page-viewer-context";
import {BymoPageViewer} from "../../../components/bymo-page-viewer/bymo-page-viewer";
import type {
    DoppeActionTypesStore
} from "../../common-client-services/doppe-action-types-store/doppe-action-types-store";
import {
    doppeActionTypesStoreLoadUsedActionTypesViewerComponents
} from "../../common-client-services/doppe-action-types-store/doppe-action-types-store";
import {
    BymoPageViewerComponentsContext
} from "../../../components/bymo-page-viewer-components-context/bymo-page-viewer-components-context";
import {doppeDtoUserGetAppVariant, doppeDtoUserGetLinkedMetaSiteId} from "../../../client-server-common";
import {BymoTemplatesIds} from "../../../client-server-common/doppe-templates/doppe-templates";
import {
    DoppeViewerLocalesContext,
    doppeViewerLocalesCreateTranslator
} from "../../doppe-viewer-locales/doppe-viewer-locales";

export async function bymoPageViewerPageAsyncCreate (
    options: {
        actionTypesStore: DoppeActionTypesStore;
        bymoTemplatesStore: BymoTemplatesStore;
        bymoLinkViewerData: DoppeDtoBymoLinkViewerData.WithConnectedPage;
        mode: BymoPageViewerLiveMode;
    }
) {
    const {
        actionTypesStore,
        bymoTemplatesStore,
        bymoLinkViewerData,
        mode,
    } = options;

    const {
        pageData: {
            bymoPageProps,
            bymoTemplateId,
            ownerData,
            premiumFeaturesStatus
        },
        bymoPagePropsOverrides,
        targetUrl,
        userDisplayName,
        userProfilePicture,
        userTermsOfServiceUrl,
        userPrivacyPolicyUrl,
        userCookieBannerSettings,
        bymoPageContextProps,
        userAccountName,
        openGraphImage,
        viewerFeatures
    } = bymoLinkViewerData;

    const bymoTemplateData = await bymoTemplatesStore.getBymoTemplateData(bymoTemplateId);

    const actionTypesViewerComponents = await doppeActionTypesStoreLoadUsedActionTypesViewerComponents({
        bymoTemplateId: bymoTemplateId,
        actionTypesStore: actionTypesStore,
        bymoPageProps: bymoPageProps
    });

    const doppeViewerLocales = await doppeViewerLocalesCreateTranslator(bymoPageProps.lang);

    return (
        <BymoPageViewerComponentsContext.Provider
            value={{
                actionTypesViewerComponents: actionTypesViewerComponents
            }}
        >
            <BymoPageViewerContext.Provider
                value={{
                    mode: mode,
                    linkId: bymoLinkViewerData.linkId,
                    bymoPageId: bymoLinkViewerData.bymoPageId,
                    doppeUserId: bymoLinkViewerData.doppeUserId,
                    pageType: doppeDtoBymoLinkViewerDataResolvePageType(bymoLinkViewerData)
                }}
            >
                <DoppeViewerLocalesContext.Provider
                    value={doppeViewerLocales}
                >
                    <BymoPageViewer
                        bymoPageComponentType={bymoTemplateData.componentType}
                        bymoPageContextProps={{
                            ...viewerFeatures,
                            targetUrl: targetUrl,
                            targetUrlImage: openGraphImage ?? null,
                            userDisplayName: userDisplayName,
                            userAccountName: userAccountName,
                            userProfilePicture: userProfilePicture,
                            userTermsOfServiceUrl: userTermsOfServiceUrl,
                            userPrivacyPolicyUrl: userPrivacyPolicyUrl,
                            userCookieBannerSettings: userCookieBannerSettings,
                            linkedMetaSiteId: doppeDtoUserGetLinkedMetaSiteId(ownerData),
                            premiumFeaturesStatus: premiumFeaturesStatus,
                            appVariant: doppeDtoUserGetAppVariant(ownerData),
                            isSpeedDialPage: bymoTemplateId === BymoTemplatesIds.SpeedDial,
                            ...bymoPageContextProps
                        }}
                        bymoPagePropsForViewer={{
                            ...bymoPageProps,
                            ...bymoPagePropsOverrides
                        }}
                        currentPageInfo={bymoLinkViewerData.viewerPageInfo}
                    />
                </DoppeViewerLocalesContext.Provider>
            </BymoPageViewerContext.Provider>
        </BymoPageViewerComponentsContext.Provider>
    )
}